import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import useUnleashFeatureFlagAdapter from '../../adapter/UnleashFeatureFlagAdapter';
import {
  FEATURE_FLAGS,
  FEATURE_FLAG_TYPES,
  type FeatureFlagType,
} from '../../constants/FeatureFlag';
import type { RootState } from '../../../redux/types';

export type UseFeatureFlagOptions = {
  /** Bypass session cache + in-flight dedupe (always hits the network). */
  skipCache?: boolean;
};

export type UseFeatureFlagResult = {
  isFeatureFlagEnable: boolean;
  flagsReady: boolean;
  flagResolved: boolean;
};

const flattenFlags = (
  flagsGroup: typeof FEATURE_FLAGS,
): readonly string[] =>
  Object.values(flagsGroup).flatMap(group => Object.values(group));

const ALL_FLAGS = flattenFlags(FEATURE_FLAGS);

// Session cache (same idea as useFeatureFlagWithContext). Flags rarely flip
// mid-session; remounts of gates/pages were causing N identical GETs.
const flagCache = new Map<string, boolean>();
const flagInflight = new Map<string, Promise<boolean>>();

export const clearFeatureFlagCache = (): void => {
  flagCache.clear();
  flagInflight.clear();
};

const resolveFlagType = (flagName: string): FeatureFlagType | null => {
  if (!ALL_FLAGS.includes(flagName)) {
    console.warn(`[useFeatureFlag] Feature flag "${flagName}" does not exist`);
    return null;
  }

  if (
    (Object.values(FEATURE_FLAGS.PERMISSION) as string[]).includes(flagName)
  ) {
    return FEATURE_FLAG_TYPES.PERMISSION;
  }

  if ((Object.values(FEATURE_FLAGS.RELEASE) as string[]).includes(flagName)) {
    return FEATURE_FLAG_TYPES.RELEASE;
  }

  return FEATURE_FLAG_TYPES.KILL_SWITCH;
};

const buildCacheKey = (
  flagName: string,
  type: FeatureFlagType | null,
  userId?: string | number,
): string => {
  if (type === FEATURE_FLAG_TYPES.PERMISSION) {
    return `${flagName}::user:${userId ?? ''}`;
  }
  return flagName;
};

/**
 * @param flagName
 * @param options.skipCache bypass session cache + in-flight dedupe.
 *   Default keeps cache — same contract as before, fewer duplicate requests.
 *   KILL_SWITCH flags always skip cache regardless of this option.
 */
const useFeatureFlag = (
  flagName?: string,
  { skipCache = false }: UseFeatureFlagOptions = {},
): UseFeatureFlagResult => {
  const { isEnabled, isUserEnabled, flagsReady } =
    useUnleashFeatureFlagAdapter();
  const userId = useSelector((state: RootState) => state.auth?.user?.id);

  const [isFeatureFlagEnable, setIsFeatureFlagEnable] = useState(false);
  const [flagResolved, setFlagResolved] = useState(false);

  useEffect(() => {
    let mounted = true;
    setFlagResolved(false);

    if (!flagName) {
      setIsFeatureFlagEnable(false);
      setFlagResolved(true);
      return () => {
        mounted = false;
      };
    }

    // Adapter hoje sempre expõe flagsReady=true; quando false, não disparamos
    // fetch (evita cachear/inflight de promise pendente que nunca resolve).
    if (!flagsReady) {
      return () => {
        mounted = false;
      };
    }

    const checkFlag = async () => {
      try {
        const type = resolveFlagType(flagName);
        if (type === null) {
          if (mounted) setIsFeatureFlagEnable(false);
          return;
        }

        const cacheKey = buildCacheKey(flagName, type, userId);
        // Kill switches must always hit the network — never cache/dedupe.
        const shouldSkipCache =
          skipCache || type === FEATURE_FLAG_TYPES.KILL_SWITCH;

        if (!shouldSkipCache && flagCache.has(cacheKey)) {
          if (mounted) setIsFeatureFlagEnable(flagCache.get(cacheKey)!);
          return;
        }

        if (!shouldSkipCache && flagInflight.has(cacheKey)) {
          const enabled = await flagInflight.get(cacheKey)!;
          if (mounted) setIsFeatureFlagEnable(enabled);
          return;
        }

        const resolveEnabled = (): Promise<boolean> =>
          type === FEATURE_FLAG_TYPES.PERMISSION
            ? isUserEnabled(flagName)
            : isEnabled(flagName);

        const fetchPromise = resolveEnabled()
          .then(enabled => {
            if (!shouldSkipCache) {
              flagCache.set(cacheKey, enabled);
              flagInflight.delete(cacheKey);
            }
            return enabled;
          })
          .catch(error => {
            if (!shouldSkipCache) {
              flagInflight.delete(cacheKey);
            }
            throw error;
          });

        if (!shouldSkipCache) {
          flagInflight.set(cacheKey, fetchPromise);
        }

        const enabled = await fetchPromise;
        if (mounted) setIsFeatureFlagEnable(enabled);
      } catch (error) {
        console.error(error);
        if (mounted) setIsFeatureFlagEnable(false);
      } finally {
        if (mounted) setFlagResolved(true);
      }
    };

    void checkFlag();

    return () => {
      mounted = false;
    };
  }, [flagName, flagsReady, isEnabled, isUserEnabled, userId, skipCache]);

  return {
    isFeatureFlagEnable,
    flagsReady,
    flagResolved,
  };
};

export default useFeatureFlag;
